import { DinerAddress, NumericString } from 'src/utilities/types';
import { copyConstructable } from './copy-constructable';

export enum AddressFrom {
  SAVED = 'select saved address',
  AUTOCOMPLETED = 'select autocompleted address',
  AUTOCOMPLETED_TO_SAVED = 'autocompleted to saved address',
  TYPED = 'typed full address',
  TYPED_TO_SAVED = 'typed to saved address',
  GEOLOCATED = 'geolocated address',
  GEOLOCATED_TO_SAVED = 'geolocated to saved address',
  AUTO_GEOLOCATED = 'auto-geolocated address',

  AUTOCOMPLETED_GENERAL = 'select autocompleted address_general',
  AUTOCOMPLETED_WIDGET_MAP = 'select autocompleted address_street address_map impression',
  AUTOCOMPLETED_WIDGET_MAP_DONE = 'select autocompleted address_street address_map confirmation cta',

  TYPED_GENERAL = 'typed full address_general',
  TYPED_WIDGET_MAP = 'typed full address_street address_map impression',
  TYPED_WIDGET_MAP_DONE = 'typed full address_street address_map confirmation cta',
}

export interface FullDinerAddress extends DinerAddress {
  label?: string;
}

export interface ConstructorOptions {
  isDinerAddress?: boolean;
}

export class Address {
  public id: string;
  public address1: string;
  public address2: string;
  public address_locality: string;
  public city: string;
  public state: string;
  public zip: string;
  public country: string;
  public label: string;
  public phone: string;
  public crossStreet: string;
  public subLocality: string;
  public latitude: NumericString;
  public longitude: NumericString;
  public latitudeString: string;
  public longitudeString: string;
  public establishment: string;
  public neighborhood: string;
  public specialInstructions: string;
  public specialInstructionsPickup: string;
  public timezoneOffset: number;
  public timezoneId: string;
  public types: string[];
  public pickupRadius: number;
  public lastUsed: Date;
  public green: boolean;
  public deliveryLabel: string;
  public deliversToAddress: boolean;
  public description: string;
  public restricted: boolean;
  public confirmDelete: boolean;
  public deleteDisabled: boolean;
  public business_address: string; // tslint:disable-line:variable-name

  public customLabel?: string;
  public save?: boolean;
  public selectedFrom?: AddressFrom;
  public selected?: boolean;

  constructor(data: any = undefined, options: ConstructorOptions = {}) {
    if (!data || copyConstructable(this, data)) {
      return;
    }
    convertObject(this, data);
  }

  public matchesDinerAddress(other: DinerAddress): boolean {
    return (
      this.latitudeString === other.latitude &&
      this.longitudeString === other.longitude
    );
  }

  public toFullLabel(prefixLabel?: boolean): string {
    let buffer = '';

    if (!this) {
      return buffer;
    }

    if (prefixLabel) {
      const nickname = this.label ? this.label : '';
      buffer += nickname !== '' ? nickname + ', ' : '';
    }

    if (this.address1) {
      buffer += this.address1 + ', ';

      if (this.address2) {
        buffer += this.address2 + ', ';
      }
    } else if (this.establishment) {
      buffer += this.establishment + ', ';
    } else if (this.neighborhood) {
      buffer += this.neighborhood + ', ';
    }

    if (this.city) {
      buffer += this.city + ', ';
    }

    if (this.state) {
      buffer += this.state + ', ';
    }

    if (this.zip) {
      buffer += this.zip + ', ';
    }

    buffer = buffer.replace(/, $/, '');

    return buffer;
  }
}

function convertObject(address: Address, data: any) {
  address.address1 =
    data.address1 || data.street_address1 || data.street_address;
  address.address2 = data.address2 || data.street_address2;
  address.state = data.state || data.address_region || data.region;
  address.city = data.city || data.address_locality || data.locality;

  if (data.address_sublocality && !address.city) {
    address.city = data.address_sublocality;
  }
  address.subLocality = ((): string => {
    let subLocality =
      data.city ||
      data.address_sublocality ||
      data.address_locality ||
      data.locality;
    // Exception for Manhattan addresses

    if (
      subLocality &&
      subLocality.toLowerCase() === 'new york' &&
      data.state &&
      data.state.toLowerCase() === 'ny'
    ) {
      subLocality = 'Manhattan';
    }

    return subLocality;
  })();
  address.zip = data.zip || data.postal_code;
  address.country = data.country || data.address_country;
  // if new timezone offset

  if (!!data.time_zone && data.time_zone.raw_offset !== undefined) {
    // Immediately convert to hours
    address.timezoneOffset = data.time_zone.raw_offset / 3600000;
    address.timezoneId = data.time_zone.id;
  } else if (data.time_zone_offset) {
    address.timezoneOffset = data.time_zone_offset / 3600000;
  }

  if ((data.latitude && data.longitude) || (data.lat && data.lng)) {
    const lat = data.latitude || data.lat;
    const lng = data.longitude || data.lng;
    address.latitudeString = `${lat}`;
    address.longitudeString = `${lng}`;
  }

  // saved address data
  address.crossStreet =
    data.cross_street || data.cross_streets || data.crossStreet;
  address.specialInstructions =
    data.special_instructions ||
    data.specialInstructions ||
    data.delivery_instructions;
  address.lastUsed = data.last_used || data.lastUsed;
  // geocoded data
  address.pickupRadius = data.pickup_radius || data.pickupRadius;

  // if we have enough for a full address default to street_address type
  if (
    !address.types &&
    address.address1 &&
    address.city &&
    address.state &&
    address.zip
  ) {
    address.types = ['street_address'];
  }
  // green
  address.green = !!data.green_indicated || !!data.green;

  Object.entries(data).forEach(([key, value]) => {
    if (!(key in Address.prototype)) {
      address[key] = value;
    }
  });
}
